import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useAppDispatch } from "../../store/hooks";
import { setCustomSelectOpen } from "../../store/reducers/customSelect";
import ResourceSelectDialog from "../Common/ResourceSelectDialog";
import Cooks from "../Cooks/Cooks";

//Common Styles
import {
  CustomTableRowTR,
  StyledTable,
  TableData,
  TableHeader,
  TableTopHeader,
  TableWrapper,
  Title,
} from "../../styles/commonStyles";
import { CustomerService } from "../../services/CustomerService";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { formatDateTime } from "../../utils/DateTimeUtils";

const customerService = new CustomerService();

const getModuleHref = (id: number | string, module: string) => {
  return `/#/${module}/${id}/show`;
};

const CustomerCookPreferences = (props: any) => {
  let [loading, setLoading] = useState<boolean>(false);
  let [customerPreferences, setCustomerPreferences] = useState<any>([]);
  let [newRows, setNewRows] = useState<Array<{ cookId: string; rank: string }>>([
    { cookId: "", rank: "" },
  ]);
  let [openCookDialog, setOpenCookDialog] = useState<boolean>(false);
  let [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
  const dispatch = useAppDispatch();

const refreshData = useCallback(
    () => {
    setLoading(true);
    customerService
        .getCustomerCookPrefernces(props?.customerId)
        .then((data) => {
        setCustomerPreferences(data);
        console.log(data);
        setLoading(false);
        })
        .catch((err: any) => {
        setLoading(false);
        });
    },
    [props?.customerId]
);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const { minRank, maxRank } = useMemo(() => {
    const ranks = customerPreferences
      .map((item: any) => Number(item?.rank))
      .filter((val: number) => Number.isFinite(val));
    if (!ranks.length) {
      return { minRank: 0, maxRank: 0 };
    }
    return {
      minRank: Math.min(...ranks),
      maxRank: Math.max(...ranks),
    };
  }, [customerPreferences]);

  const getRankBg = (rankValue: any) => {
    const rank = Number(rankValue);
    if (!Number.isFinite(rank)) return "transparent";
    if (minRank === maxRank) return "hsl(120 55% 94%)";
    const t = (rank - minRank) / (maxRank - minRank); // low -> green, high -> light green
    const lightness = 88 + 8 * t;
    return `hsl(120 55% ${lightness}%)`;
  };

  const updateRow = (index: number, key: "cookId" | "rank", value: string) => {
    setNewRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    );
  };

  const addRow = () => {
    setNewRows((prev) => [...prev, { cookId: "", rank: "" }]);
  };

  const submitRows = () => {
    const payload = newRows.map((row) => ({
      cookId: Number(row.cookId.trim()),
      rank: Number(row.rank.trim()),
    })).filter(row => row.cookId && row.rank);
    if (!payload.length) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Please enter valid cook IDs and ranks before submitting.",
        type: CustomSnackbarTypes.WARNING,
      });
      return;
    }
    customerService.updateCustomerCookPreferences(props?.customerId, payload).then((data)=>{
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
            message: "Cook preferences updated successfully",
            type: CustomSnackbarTypes.SUCCESS,
        });
        refreshData();
        setNewRows([{ cookId: "", rank: "" }]);
    }).catch(error=>{
        console.log(error);
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
            message: "Failed to update cook preferences. Please try again.",
            type: CustomSnackbarTypes.ERROR,
        });
    });
  };

  const handleOpenCookDialog = (index: number) => {
    setActiveRowIndex(index);
    setOpenCookDialog(true);
    dispatch(
      setCustomSelectOpen({
        open: true,
      }),
    );
  };

  const handleCloseCook = (record: any) => {
    setOpenCookDialog(false);
    if (activeRowIndex === null) return;
    const cookId =
      record?.cookFullProfile?.cookId ??
      record?.cookId ??
      record?.id ??
      "";
    updateRow(activeRowIndex, "cookId", String(cookId));
  };

  return (
    <>
      <TableWrapper>
        <TableTopHeader>
          <Title>{'Cook Ranks'}</Title>
        </TableTopHeader>

        <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Add cook ranks
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Add multiple cook IDs with ranks. Use + / − to change rows.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <IconButton size="small" onClick={addRow} aria-label="add row">
                <AddIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => setNewRows((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev))}
                aria-label="remove row"
                disabled={newRows.length === 1}
              >
                <RemoveIcon />
              </IconButton>
              <Button variant="contained" size="small" onClick={submitRows}>
                Submit
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {newRows.map((row, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    minWidth: 28,
                    height: 28,
                    borderRadius: "50%",
                    bgcolor: "action.selected",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  {index + 1}
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    width: { xs: "100%", sm: 220 },
                    justifyContent: "flex-start",
                    height: 40,
                  }}
                  onClick={() => handleOpenCookDialog(index)}
                  color="primary"
                >
                  {row.cookId ? `Cook ID: ${row.cookId}` : "Choose Cook"}
                </Button>
                <TextField
                    label="Cook Rank"
                    size="small"
                    value={row.rank}
                    onChange={(e) => updateRow(index, "rank", e.target.value)}
                    sx={{
                        width: { xs: "100%", sm: 220 },
                        "& .MuiOutlinedInput-root": {
                        height: 40,
                        borderRadius: "10px",
                        backgroundColor: "background.paper",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "divider",
                        },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "text.primary",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                        borderWidth: 2,
                        },
                        "& .MuiInputLabel-root": {
                        fontWeight: 600,
                        },
                    }}
                    />

                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => {
                    setNewRows((prev) => prev.filter((_, i) => i !== index));
                  }}
                  disabled={newRows.length === 1}
                  aria-label="delete row"
                >
                  <RemoveIcon />
                </IconButton>
              </Paper>
            ))}
          </Box>
        </Paper>
        {openCookDialog && (
          <ResourceSelectDialog
            name={"Cooks"}
            list={Cooks}
            onClose={handleCloseCook}
          />
        )}

        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            overflowX: "auto",
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
          }}
        >
          <StyledTable size="small">
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "rgba(232, 235, 237, 0.3) !important",
                }}
              >
                <TableHeader>Cook ID</TableHeader>
                <TableHeader>Cook Name</TableHeader>
                <TableHeader>Rank</TableHeader>
                <TableHeader>Updated By</TableHeader>
                <TableHeader>Updated At</TableHeader>
                <TableHeader align="right">Action</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && customerPreferences.length ? (
                customerPreferences.map((request: any) => {
                  return (
                    <CustomTableRowTR
                      key={request.id}
                      sx={{
                        "--row-bg": getRankBg(request?.rank),
                      }}
                    >
                      <TableData>
                        {request?.cookId ? (
                            <Link
                            href={getModuleHref(request.cookId, "Cooks")}
                            underline="hover"
                            >
                            {request.cookId}
                            </Link>
                        ) : (
                            "-"
                        )}
                      </TableData>
                      <TableData>{`${request?.cook?.firstName} ${request?.cook?.lastName}`}</TableData>
                      <TableData>
                        {request?.rank ? request.rank : "-"}
                      </TableData>
                      <TableData>
                        {request?.updatedBy ? (
                            <Link
                            href={getModuleHref(request.updatedBy, "AdminUsers")}
                            underline="hover"
                            >
                            {request.updatedBy}
                            </Link>
                        ) : (
                            "-"
                        )}
                      </TableData>
                      <TableData>
                        {request.updatedAt
                          ? formatDateTime(request.updatedAt)
                          : "-"}
                      </TableData>
                      <TableData align="right">
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            width: 176,
                            height: 32,
                            whiteSpace: "nowrap",
                            textTransform: "none",
                            px: 1,
                          }}
                          onClick={()=>{
                            EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
                            message: "Are you sure you want to remove this cook from preference list?",
                            onClose: (confirmed: boolean) => {
                            customerService.removeFromPrefernce(Number(request?.id)).then((data)=>{
                                EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                                message: "Cook removed from preference list successfully",
                                type: CustomSnackbarTypes.SUCCESS,
                                });
                                window.location.reload();
                            }).catch(error=>{
                                console.log(error);
                            })
                            },
                        });
                          }}
                        >
                            Delete
                        </Button>
                      </TableData>
                    </CustomTableRowTR>
                  );
                })
              ) : !loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                    No Prefferd Chef found.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </TableWrapper>
    </>
  );
};

export default CustomerCookPreferences;
